import React, { useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api, trackEvent } from "../lib/api";
import PeriodicTile from "../components/PeriodicTile";
import PerfumeBottle from "../components/PerfumeBottle";
import OrderDialog from "../components/OrderDialog";
import { useCart } from "../context/CartContext";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";

const computePrice = (pricing, sizeLabel, numScents) => {
  if (!pricing) return 0;
  const sz =
    pricing.sizes.find((s) => s.label === sizeLabel) || pricing.sizes[0];
  const base = sz?.price || 0;
  const included = sz?.included_scents ?? pricing.included_scents ?? 1;
  const extras = Math.max(0, numScents - included);
  return (
    Math.round((base + extras * (pricing.extra_scent_price || 0)) * 100) / 100
  );
};

const Builder = () => {
  const [scents, setScents] = useState([]);
  const [pricing, setPricing] = useState(null);
  const [featured, setFeatured] = useState([]);
  const [bottleSize, setBottleSize] = useState("20ml");
  const [selected, setSelected] = useState([]);
  const [blendName, setBlendName] = useState("");
  const [creatorName, setCreatorName] = useState("");
  const [saving, setSaving] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [orderOpen, setOrderOpen] = useState(false); // legacy — checkout is paused during pre-launch
  const [inventory, setInventory] = useState(null);
  const { add: addToCart, clear: clearCart } = useCart();
  const navigate = useNavigate();
  const notebookRef = useRef(null);
  const spiralRef = useRef(null);
  const [searchParams] = useSearchParams();

  useEffect(() => {
    Promise.all([api.get("/scents"), api.get("/pricing"), api.get("/featured-blends"), api.get("/inventory")])
      .then(([sR, pR, fR, iR]) => {
        setScents(sR.data);
        setPricing(pR.data);
        setFeatured(fR.data);
        setInventory(iR.data);
        setLoaded(true);
        // Pre-select from ?notes= query (used by "Try this blend" CTAs)
        const presetSymbols = searchParams.get("notes");
        const presetName = searchParams.get("name");
        if (presetSymbols) {
          const wanted = presetSymbols.split(",").map((s) => s.trim());
          const ids = wanted
            .map((sym) => sR.data.find((sc) => sc.symbol === sym)?.id)
            .filter(Boolean)
            .slice(0, 5);
          setSelected(ids);
          if (presetName) setBlendName(presetName);
          if (ids.length) {
            setTimeout(
              () =>
                toast.success(
                  `Loaded "${presetName || "blend"}" — now make it yours.`
                ),
              250
            );
          }
        }
      })
      .catch(() => setLoaded(true));
    trackEvent("pageview");

    // Handle return-from-Stripe: poll /checkout/status/{session_id} until paid or expired.
    const sessionId = searchParams.get("session_id");
    if (sessionId) {
      let cancelled = false;
      let pending;
      try {
        pending = JSON.parse(
          sessionStorage.getItem("briology_pending_checkout") || "null"
        );
      } catch (_) {
        pending = null;
      }
      const cleanUrl = () => {
        try {
          const u = new URL(window.location.href);
          u.searchParams.delete("session_id");
          window.history.replaceState({}, "", u.pathname + (u.search ? u.search : ""));
        } catch (_) {
          // URL update is a nice-to-have; failure is silent
        }
      };
      const poll = async (attempt = 0) => {
        if (cancelled) return;
        try {
          const { data } = await api.get(`/checkout/status/${sessionId}`);
          if (data.payment_status === "paid") {
            const isCart = !!pending?.cart;
            const itemCount = pending?.item_count || 1;
            const cartMsg =
              "Payment received — " +
              itemCount +
              " item" +
              (itemCount === 1 ? "" : "s") +
              " on the way! Check your inbox for confirmation.";
            const blendMsg =
              "Payment received — your " +
              (pending?.blend_name ? '"' + pending.blend_name + '" ' : "") +
              "blend is on its way! Check your inbox for confirmation.";
            toast.success(isCart ? cartMsg : blendMsg, { duration: 6000 });
            sessionStorage.removeItem("briology_pending_checkout");
            if (isCart) clearCart();
            setSelected([]);
            setBlendName("");
            cleanUrl();
            return;
          }
          if (data.status === "expired" || data.payment_status === "expired") {
            toast.error("Payment session expired — please try again.");
            sessionStorage.removeItem("briology_pending_checkout");
            cleanUrl();
            return;
          }
          if (attempt >= 6) {
            toast("Still processing — we'll email you the moment it clears.", {
              duration: 5000,
            });
            cleanUrl();
            return;
          }
          setTimeout(() => poll(attempt + 1), 2000);
        } catch (_) {
          if (attempt >= 3) {
            cleanUrl();
            return;
          }
          setTimeout(() => poll(attempt + 1), 2500);
        }
      };
      toast("Confirming your payment…", { duration: 3000 });
      poll();
      return () => {
        cancelled = true;
      };
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const selectedScents = useMemo(
    () => selected.map((id) => scents.find((s) => s.id === id)).filter(Boolean),
    [selected, scents]
  );

  const currentSize = useMemo(
    () => pricing?.sizes?.find((s) => s.label === bottleSize),
    [pricing, bottleSize]
  );
  const includedScents = currentSize?.included_scents ?? 1;
  const price = useMemo(
    () => computePrice(pricing, bottleSize, selected.length),
    [pricing, bottleSize, selected.length]
  );
  const extraCount = Math.max(0, selected.length - includedScents);

  const toggle = (scent) => {
    trackEvent("scent_click", { scent_id: scent.id });
    setSelected((prev) => {
      if (prev.includes(scent.id)) {
        trackEvent("scent_deselect", { scent_id: scent.id });
        return prev.filter((id) => id !== scent.id);
      }
      if (prev.length >= 5) {
        toast.error("Max 5 notes per bottle — remove one first", {
          duration: 1800,
        });
        return prev;
      }
      trackEvent("scent_select", { scent_id: scent.id });
      // If picking beyond included, toast a friendly heads-up (only on the first extra)
      if (prev.length === includedScents) {
        toast(
          `Just a heads-up: each note past ${includedScents} adds $${pricing?.extra_scent_price || 5}.`,
          { duration: 2400 }
        );
      }
      return [...prev, scent.id];
    });
  };

  const clearAll = () => setSelected([]);

  const applyFeaturedBlend = (fb) => {
    const ids = fb.scent_symbols
      .map((sym) => scents.find((sc) => sc.symbol === sym)?.id)
      .filter(Boolean)
      .slice(0, 5);
    setSelected(ids);
    setBlendName(fb.name);
    trackEvent("featured_blend_load", { blend: fb.name });
    toast.success(`Loaded "${fb.name}" — now make it yours.`);
  };

  const handleSave = async () => {
    if (!blendName.trim()) {
      toast.error("Give your blend a name");
      return;
    }
    if (selected.length === 0) {
      toast.error("Pick at least one note");
      return;
    }
    try {
      setSaving(true);
      await api.post("/blends", {
        name: blendName.trim(),
        scent_ids: selected,
        creator_name: creatorName.trim() || "",
      });
      toast.success(`"${blendName}" bottled. ✨`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not save blend");
    } finally {
      setSaving(false);
    }
  };

  const handleOrder = async () => {
    if (selected.length === 0) {
      toast.error("Pick at least one note");
      return;
    }
    // Re-check inventory RIGHT before opening the OrderDialog so we don't let
    // a customer who camped on the page through a sell-out sneak past the cap.
    try {
      const { data } = await api.get("/inventory");
      setInventory(data);
      if (data.sold_out) {
        toast.error("Temporarily sold out — check back after the next drop.");
        return;
      }
    } catch (e) {
      // Network hiccup — allow the flow; backend guard will 409 if truly sold out
      console.error("Builder.handleOrder: inventory re-check failed", e);
    }
    setOrderOpen(true);
  };

  const handleAddToCart = async () => {
    if (inventory?.sold_out) {
      toast.error("Temporarily sold out — check back after the next drop.");
      return;
    }
    if (selected.length === 0) {
      toast.error("Pick at least one note");
      return;
    }
    // Refresh inventory so a stale mount doesn't let a sold-out user add.
    try {
      const { data } = await api.get("/inventory");
      setInventory(data);
      if (data.sold_out) {
        toast.error("Temporarily sold out — check back after the next drop.");
        return;
      }
    } catch (e) {
      // Network hiccup — proceed; checkout endpoint will 409 if truly sold out
      console.error("Builder.handleAddToCart: inventory re-check failed", e);
    }
    const symbols = selected
      .map((id) => scents.find((s) => s.id === id)?.symbol)
      .filter(Boolean);
    const name = blendName.trim() || "Custom Blend";
    addToCart({
      blend_name: name,
      bottle_size: bottleSize,
      scent_ids: [...selected],
      scent_symbols: symbols,
      price: computePrice(pricing, bottleSize, selected.length),
    });
    toast.success("Added to cart", {
      description: `${name} · ${bottleSize}`,
      action: {
        label: "View cart",
        onClick: () => navigate("/cart"),
      },
    });
  };

  return (
    <div className="relative max-w-[1400px] mx-auto px-4 sm:px-6 md:px-10 pb-24">
      {/* Hero — horizontal polaroid row: photo · logo · photo, with steps card below */}
      <section
        data-testid="hero-polaroid-row"
        className="relative pt-8 md:pt-14 pb-8 md:pb-14"
      >
        {/* Cheetah-print backdrop — bleeds edge to edge behind polaroids + steps */}
        <div
          aria-hidden
          data-testid="cheetah-backdrop"
          className="pointer-events-none absolute inset-0 left-1/2 -translate-x-1/2 w-screen"
          style={{
            backgroundImage: 'url("/assets/cheetah_bg.jpg")',
            backgroundSize: "cover",
            backgroundPosition: "center",
            backgroundRepeat: "no-repeat",
            zIndex: 0,
          }}
        >
          {/* Very light warm veil so white polaroids still separate from the fur */}
          <div
            className="absolute inset-0"
            style={{
              background:
                "linear-gradient(180deg, rgba(255,247,235,0.05) 0%, rgba(255,247,235,0.10) 100%)",
            }}
          />
        </div>

        <div className="relative z-10">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-6 items-center">
          {/* LEFT — Photo 1 (smaller, original scale) */}
          <figure
            data-testid="hero-polaroid-1"
            className="relative bg-white pt-5 px-5 pb-14 mx-auto w-full max-w-[420px]"
            style={{
              transform: "rotate(-3deg)",
              boxShadow:
                "0 22px 34px -14px rgba(60,40,20,0.30), 0 8px 14px -6px rgba(60,40,20,0.18)",
            }}
          >
            <img
              src="/assets/briology_hero.png"
              alt="Briology · custom perfume oil"
              draggable={false}
              className="block w-full aspect-[3/2] bg-[#F4E5C9]"
              style={{ objectFit: "cover", objectPosition: "50% 50%" }}
            />
          </figure>

          {/* CENTER — Logo polaroid (smaller, matches left) */}
          <figure
            data-testid="hero-polaroid-logo"
            className="relative bg-white pt-5 px-5 pb-14 mx-auto w-full max-w-[420px]"
            style={{
              transform: "rotate(1.4deg)",
              boxShadow:
                "0 22px 34px -14px rgba(60,40,20,0.30), 0 8px 14px -6px rgba(60,40,20,0.18)",
            }}
          >
            <div className="w-full aspect-[3/2] bg-white flex items-center justify-center overflow-hidden">
              <img
                src="/assets/briology_logo.jpg"
                alt="Briology logo"
                draggable={false}
                className="max-w-[92%] max-h-[92%] w-auto h-auto"
                style={{ objectFit: "contain" }}
              />
            </div>
          </figure>

          {/* RIGHT — Photo 2 (matched frame, contain to show entire face) */}
          <figure
            data-testid="hero-polaroid-2"
            className="relative bg-white pt-5 px-5 pb-14 mx-auto w-full max-w-[420px]"
            style={{
              transform: "rotate(2.6deg)",
              boxShadow:
                "0 22px 34px -14px rgba(60,40,20,0.30), 0 8px 14px -6px rgba(60,40,20,0.18)",
            }}
          >
            <img
              src="/assets/briology_hero_2.jpg"
              alt="Briology · signature blend with dropper"
              draggable={false}
              className="block w-full aspect-[3/2] bg-[#F4E5C9]"
              style={{ objectFit: "cover", objectPosition: "50% 50%" }}
            />
          </figure>
        </div>

        {/* Instructions / steps — restored below the polaroid row */}
        <div
          data-testid="hero-instructions"
          className="mt-10 md:mt-14 max-w-2xl mx-auto bg-white border border-black/15 brutal-shadow px-5 py-4 md:px-6 md:py-5"
        >
          <h1
            data-testid="brand-quote"
            className="font-serif-display font-light text-lg md:text-xl leading-[1.5] text-black text-center"
          >
            Reviving your identity, one drop at a time.
          </h1>
          <p className="mt-3 text-sm md:text-[15px] text-black/80 leading-snug text-center">
            Pick your bottle, then tap up to five fragrances from our periodic
            table. Watch them layer into a bottle in real time. Save it.
          </p>
          <ol className="mt-3 pt-3 border-t border-black/15 flex flex-col sm:flex-row sm:items-center sm:justify-center sm:flex-wrap gap-x-5 gap-y-1">
            <li className="overline text-[10px] text-black/70 flex items-center gap-1.5">
              <span className="text-[#FF1493] font-semibold">01</span> Choose bottle
            </li>
            <li className="hidden sm:block text-black/30">·</li>
            <li className="overline text-[10px] text-black/70 flex items-center gap-1.5">
              <span className="text-[#FF1493] font-semibold">02</span> Pick notes
            </li>
            <li className="hidden sm:block text-black/30">·</li>
            <li className="overline text-[10px] text-black/70 flex items-center gap-1.5">
              <span className="text-[#FF1493] font-semibold">03</span> Save
            </li>
          </ol>
        </div>
        </div>{/* /relative z-10 wrapper */}
      </section>

      {/* Notebook page — spans edge-to-edge with the spiral sitting on its outer left. */}
      <div className="relative mt-4">
        <div
          ref={notebookRef}
          data-testid="notebook-page"
          className="notebook-page relative ml-[46px] px-4 sm:px-6 md:px-10 pt-8 pb-14 sm:pt-10 sm:pb-16"
        >
          <div ref={spiralRef} className="site-spiral is-visible" aria-hidden />

        {/* Bottle-size selector (upfront) */}
        <section
          data-testid="size-picker"
          className="mb-8 grid grid-cols-2 gap-3 sm:gap-4"
        >
        {(pricing?.sizes || [{ label: "20ml", price: 55 }, { label: "30ml", price: 70 }]).map(
          (s) => {
            const active = bottleSize === s.label;
            return (
              <button
                key={s.label}
                data-testid={`builder-size-${s.label}`}
                type="button"
                onClick={() => setBottleSize(s.label)}
                className={[
                  "paper-card text-left p-4 sm:p-5 transition-all",
                  active
                    ? "!bg-black !text-white"
                    : "",
                ].join(" ")}
                style={active ? { boxShadow: "0 14px 30px -12px rgba(255,20,147,0.45), 0 3px 6px rgba(0,0,0,0.15)" } : undefined}
              >
                <div className="flex items-baseline justify-between">
                  <div className="font-serif-display text-2xl sm:text-3xl font-black">
                    {s.label}
                  </div>
                  <div className="font-serif-display text-2xl sm:text-3xl font-black text-[#FF1493]">
                    ${s.price}
                  </div>
                </div>
                <div
                  className={[
                    "overline text-[9px] sm:text-[10px] mt-2 leading-tight",
                    active ? "text-white/70" : "text-black/60",
                  ].join(" ")}
                >
                  Incl. {s.included_scents ?? 1} scents · extra +${pricing?.extra_scent_price ?? 5}
                </div>
              </button>
            );
          }
        )}
      </section>

      {/* Builder grid */}
      <div className="grid lg:grid-cols-12 gap-8 lg:gap-12">
        {/* Periodic table */}
        <div className="lg:col-span-8">
          {featured.length > 0 && (
            <section
              data-testid="featured-strip"
              className="relative mb-10 pt-3 pb-1"
            >
              <div className="flex items-baseline justify-between mb-4">
                <div>
                  <h3 className="scrapbook-heading text-3xl md:text-4xl">
                    Popular blends
                  </h3>
                  <span className="scrapbook-underline" />
                </div>
                <span className="overline text-[9px] text-black/50 hidden sm:block">
                  Tap to pre-load · still fully editable
                </span>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4 md:gap-5">
                {featured.map((fb, i) => {
                  const fbScents = fb.scent_symbols
                    .map((sym) => scents.find((s) => s.symbol === sym))
                    .filter(Boolean);
                  const tilt = i % 2 === 0 ? "-rotate-[0.6deg]" : "rotate-[0.8deg]";
                  return (
                    <button
                      key={fb.id}
                      type="button"
                      data-testid={`featured-blend-${fb.name.replace(/ /g, "-")}`}
                      onClick={() => applyFeaturedBlend(fb)}
                      className={`paper-card text-left p-4 flex flex-col relative min-h-[260px] ${tilt} group`}
                      style={{ overflow: "visible" }}
                    >
                      <span className="pushpin pushpin--pink pushpin--top-c" aria-hidden />
                      <div className="overline text-[8.5px] text-[#FF1493] tracking-[0.28em]">
                        {fb.category}
                      </div>
                      <div className="font-serif-display italic text-2xl leading-tight mt-1">
                        {fb.name}
                      </div>
                      {fb.tagline && (
                        <div className="text-[11px] text-black/60 italic mt-1.5 line-clamp-2 leading-snug">
                          {fb.tagline}
                        </div>
                      )}
                      <div className="h-7 mt-3 flex overflow-hidden rounded-[1px]">
                        {fbScents.map((s) => (
                          <div
                            key={s.id}
                            className="flex-1"
                            style={{
                              background: `linear-gradient(180deg, ${s.color}, ${s.color}aa)`,
                            }}
                            title={s.name}
                          />
                        ))}
                      </div>
                      <div className="overline text-[8.5px] text-black/55 mt-2 line-clamp-1">
                        {fb.scent_symbols.join(" · ")}
                      </div>

                      {/* Hover overlay — clean white card with the full brand caption. */}
                      {fb.description && (
                        <div
                          data-testid={`featured-hover-${fb.name.replace(/ /g, "-")}`}
                          className="absolute inset-0 flex flex-col p-5 bg-white opacity-0 group-hover:opacity-100 pointer-events-none overflow-hidden"
                        >
                          <div className="overline text-[8.5px] text-[#FF1493] tracking-[0.28em]">
                            {fb.category}
                          </div>
                          <div className="font-serif-display italic text-xl leading-tight mt-1 mb-2">
                            {fb.name}
                          </div>
                          <p className="text-[11.5px] text-black/78 leading-[1.55] font-sans-display overflow-y-auto flex-1 pr-1">
                            {fb.description}
                          </p>
                        </div>
                      )}
                    </button>
                  );
                })}
              </div>
            </section>
          )}

          <div className="relative flex items-end justify-between mb-4 pt-2">
            <div>
              <div className="scrapbook-eyebrow text-[#FF1493]">
                The workspace
              </div>
              <h2 className="scrapbook-heading text-4xl md:text-5xl mt-1">
                The Table
              </h2>
              <span className="scrapbook-underline" />
            </div>
            <div className="flex items-center gap-3">
              <span data-testid="selection-counter" className="overline text-[11px]">
                {selected.length} / 5 selected
              </span>
              <button
                data-testid="clear-btn"
                onClick={clearAll}
                disabled={selected.length === 0}
                className="overline text-[10px] underline-offset-4 hover:underline disabled:opacity-30"
              >
                Clear
              </button>
            </div>
          </div>

          {!loaded ? (
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3 md:gap-4">
              {Array.from({ length: 12 }).map((_, i) => (
                <div
                  key={i}
                  className="aspect-square bg-white/70 border border-black/30 animate-pulse"
                />
              ))}
            </div>
          ) : (
            <div
              data-testid="periodic-table"
              className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3 md:gap-4"
            >
              {scents.map((s, i) => (
                <PeriodicTile
                  key={s.id}
                  scent={s}
                  index={i}
                  selected={selected.includes(s.id)}
                  disabled={!selected.includes(s.id) && selected.length >= 5}
                  onClick={toggle}
                />
              ))}
            </div>
          )}
        </div>

        {/* Visualizer */}
        <aside className="lg:col-span-4">
          <div className="lg:sticky lg:top-24">
            <div
              className="paper-card relative p-6 md:p-8"
            >
              {/* Live price strip */}
              <div
                data-testid="live-price"
                className="flex items-center justify-between border border-black bg-[#FFF1F5] px-4 py-3 mb-6"
              >
                <div>
                  <div className="overline text-[10px] text-black/70">
                    {bottleSize} · {selected.length} note
                    {selected.length === 1 ? "" : "s"}
                    {extraCount > 0 && (
                      <span className="text-[#FF1493]">
                        {" "}
                        (+{extraCount} extra)
                      </span>
                    )}
                  </div>
                  <div className="font-serif-display text-3xl font-black text-[#FF1493] leading-none mt-0.5">
                    ${price.toFixed(2)}
                  </div>
                </div>
                <button
                  onClick={() =>
                    setBottleSize(bottleSize === "20ml" ? "30ml" : "20ml")
                  }
                  className="overline text-[10px] underline-offset-4 hover:underline"
                >
                  swap size
                </button>
              </div>

              <PerfumeBottle
                selectedScents={selectedScents}
                blendName={blendName}
              />

              {/* Notes list */}
              <div className="mt-6 space-y-1.5 min-h-[80px]" data-testid="notes-list">
                {selectedScents.length === 0 ? (
                  <div className="text-sm text-black/50 italic">
                    No notes selected — tap a tile to begin.
                  </div>
                ) : (
                  selectedScents.map((s, i) => (
                    <div
                      key={s.id}
                      className="flex items-center justify-between text-sm"
                    >
                      <div className="flex items-center gap-2">
                        <span
                          className="inline-block w-3 h-3 rounded-full border border-black/60"
                          style={{ background: s.color }}
                        />
                        <span className="overline text-[10px]">
                          {i === 0 ? "Top" : i === selectedScents.length - 1 ? "Base" : "Heart"}
                        </span>
                        <span>{s.name}</span>
                      </div>
                      <button
                        onClick={() => toggle(s)}
                        data-testid={`remove-${s.symbol}`}
                        className="text-xs text-black/50 hover:text-[#FF1493]"
                      >
                        remove
                      </button>
                    </div>
                  ))
                )}
              </div>

              {/* Save form */}
              <div className="mt-6 pt-6 border-t border-black/20">
                <button
                  data-testid="add-to-cart-btn"
                  onClick={handleAddToCart}
                  disabled={inventory?.sold_out}
                  className={
                    inventory?.sold_out
                      ? "w-full bg-black/30 text-white font-sans-display uppercase tracking-[0.18em] text-sm py-4 px-6 rounded-full border border-black cursor-not-allowed"
                      : "w-full bg-[#FF1493] text-white font-sans-display uppercase tracking-[0.18em] text-sm py-4 px-6 rounded-full hover:bg-black transition-colors duration-300 border border-black"
                  }
                >
                  {inventory?.sold_out ? "Temporarily Sold Out" : "Add to cart"}
                </button>
                {inventory?.sold_out ? (
                  <p
                    data-testid="sold-out-note"
                    className="mt-3 text-[11px] text-black/60 italic leading-snug text-center"
                  >
                    We&apos;ve sold out of this batch — sign up for the
                    newsletter to catch the next drop.
                  </p>
                ) : (
                  <button
                    data-testid="reserve-btn"
                    onClick={handleOrder}
                    className="mt-2 w-full bg-transparent text-black overline text-[11px] py-3 px-6 rounded-full border border-black hover:bg-black hover:text-white transition-colors duration-300"
                  >
                    Quick checkout
                  </button>
                )}
              </div>
            </div>
          </div>
        </aside>
      </div>
      </div>{/* /notebook-page */}
      </div>{/* /notebook-outer */}

      <OrderDialog
        open={orderOpen}
        onClose={() => setOrderOpen(false)}
        blendName={blendName}
        scentIds={selected}
        initialSize={bottleSize}
      />
    </div>
  );
};

export default Builder;
